/**
 * The meeting's sign-in sheet (Doors in Part 2; Print.dc.html `signin`, Oct 14): the chairs who
 * said they are coming first, each with a box and a line to initial; then everybody else with what
 * they said; then blank lines for walk-ins. The room still decides who was there — the sheet is
 * what the volunteer chairs carry in.
 */
import type { ReactNode } from 'react';
import { Paper, PrintRoot, formatDate, formatDateTime, type PaperRow } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';

interface Rsvps {
  yes: number;
  waiting: number;
  byArea: { areaName: string; yes: number; waiting: number }[];
  unanswered: { personId: string; name: string; areaName: string }[];
  coChairs: { name: string; by: string }[];
}
interface One {
  meeting: { id: string; title: string; at: string; festivalYear: number };
  attendance: { personId: string; rsvp: string | null; coChairName: string | null }[];
  chairs: { personId: string; name: string; role: string }[];
}

export function SignInSheet({ id }: { id: string }): ReactNode {
  const one = useLoad<One>(`/admin/meetings/${id}`);
  const rsvps = useLoad<Rsvps>(`/admin/meetings/${id}/rsvps`);
  if (one.error) return <p role="alert">{one.error}</p>;
  const d = one.data;
  if (!d) return <p>Reading the meeting…</p>;
  const answerOf = (personId: string): string | null =>
    d.attendance.find((a) => a.personId === personId)?.rsvp ?? null;
  const coming = d.chairs.filter((c) => answerOf(c.personId) === 'yes');
  const rest = d.chairs.filter((c) => answerOf(c.personId) !== 'yes');
  const notes = d.chairs.filter((c) => answerOf(c.personId) === 'notes').length;
  const bringing = (personId: string): string | null =>
    d.attendance.find((a) => a.personId === personId)?.coChairName ?? null;
  const rows: PaperRow[] = [
    ...coming.map((c) => {
      const co = bringing(c.personId);
      return {
        key: c.personId,
        a: c.name,
        b: [c.role, 'yes', co ? `bringing ${co}` : null].filter(Boolean).join(' · '),
        c: '____',
        box: true,
      };
    }),
    ...rest.map((c) => {
      const said = answerOf(c.personId);
      return {
        key: c.personId,
        a: c.name,
        b: [c.role, said === null ? null : said === 'notes' ? 'wants the notes' : 'not coming']
          .filter(Boolean)
          .join(' · '),
        c: said === null ? '____' : '—',
        box: said === null,
      };
    }),
    ...[1, 2, 3].map((n) => ({
      key: `walk-in-${String(n)}`,
      a: 'Walk-in',
      b: n === 1 ? 'print name and area' : undefined,
      c: '____________',
      box: true,
    })),
  ];
  return (
    <PrintRoot
      kind="letter"
      title={`Sign-in — ${d.meeting.title}`}
      pages={[
        <Paper
          key="1"
          masthead={`${d.meeting.title} — ${formatDate(d.meeting.at)}`}
          submast={formatDateTime(d.meeting.at, { year: true })}
          stamp={`${String(coming.length)} YES · ${String(notes)} NOTES`}
          rows={rows}
          foot={`Signing here marks attendance — tick as people arrive. ${String(coming.length)} said they are coming of ${String(d.chairs.length)} on this year’s list.${
            (rsvps.data?.coChairs ?? []).length
              ? ` Bringing a co-chair: ${(rsvps.data?.coChairs ?? []).map((c) => c.name).join(' · ')}.`
              : ''
          }`}
        />,
      ]}
    />
  );
}
